import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface ReportSettings {
  // Detail Level
  showDetailedBreakdown: boolean; // Controls table detail level (unit prices, etc.)
  includeLaborRates: boolean;
  showTaxDetails: boolean;
  showOverheadMarkup: boolean;
  showBaseCostColumn: boolean; // Controls if Base Cost column is shown in Cost Summary

  // Section Visibility
  showMaterialsSection: boolean;
  showQuotesSection: boolean;
  showLaborSection: boolean;
  showEquipmentSection: boolean;
  showSubcontractorSection: boolean;
  showGeneralExpensesSection: boolean;

  // Contractual Clauses Sections
  showGlobalSupplyChainClause: boolean;
  showTariffsAndDutiesClause: boolean;
  showContractualClausesSection: boolean;
  showStandardContractTerms: boolean;

  // Additional Customizations
  showItemBreakouts: boolean;
  showQuantityDetails: boolean;
  showUnitCosts: boolean;
  showLaborHours: boolean;
  showBurdenDetails: boolean;
  showAdjustmentDetails: boolean;
  compactView: boolean;
  showNotes: boolean;

  // Project totals display
  showProjectTotalsBreakdown: boolean; // Controls if subtotal/overhead/profit are shown separately
  showSubtotalInCostSummary: boolean; // Controls if subtotal row is shown in Cost Summary (false = show Total Amount only)

  // Cost Summary Category Controls
  showExtensionMaterialsInCostSummary: boolean;
  showQuotedMaterialsInCostSummary: boolean;
  showLaborInCostSummary: boolean;
  showEquipmentInCostSummary: boolean;
  showSubcontractorsInCostSummary: boolean;
  showGeneralExpensesInCostSummary: boolean;

  // Extension Materials Aggregation
  showExtensionBreakouts: boolean; // Controls if extension materials show individual breakouts (true = more detail, false = aggregate by name)

  // Notes Display
  showNotesInTables: boolean; // Controls if notes columns are shown in report sections

  // Expiration Date Settings
  showExpirationDate: boolean; // Controls if expiration date is shown in the report
  expirationDays: number; // Number of days from today for expiration (30, 45, 60, etc.)
  customExpirationDate: Date | null; // Custom expiration date set by user (overrides expirationDays if set)
}

export interface ReportPreset {
  id: string;
  name: string;
  description: string;
  settings: ReportSettings;
  isDefault?: boolean;
  createdAt: Date;
  lastUsed?: Date;
}

interface ReportPresetsStore {
  // Current settings
  currentSettings: ReportSettings;

  // Presets
  presets: ReportPreset[];
  activePresetId: string | null;
  isModified: boolean; // Track if current settings differ from active preset

  // Actions
  updateSetting: <K extends keyof ReportSettings>(key: K, value: ReportSettings[K]) => void;
  updateSettings: (settings: Partial<ReportSettings>) => void;

  // Preset actions
  savePreset: (name: string, description?: string) => void;
  updatePreset: (presetId: string, name?: string, description?: string) => void;
  loadPreset: (presetId: string) => void;
  deletePreset: (presetId: string) => void;
  duplicatePreset: (presetId: string, newName: string) => void;

  // Quick presets
  applyDetailedPreset: () => void;
  applySimplePreset: () => void;
  applyMinimalPreset: () => void;

  // Utility
  hasUnsavedChanges: () => boolean;
  getActivePreset: () => ReportPreset | null;

  // Reset
  resetToDefaults: () => void;
}

const defaultSettings: ReportSettings = {
  // Detail Level
  showDetailedBreakdown: true,
  includeLaborRates: true,
  showTaxDetails: true,
  showOverheadMarkup: true,
  showBaseCostColumn: true,

  // Section Visibility
  showMaterialsSection: true,
  showQuotesSection: true,
  showLaborSection: true,
  showEquipmentSection: true,
  showSubcontractorSection: true,
  showGeneralExpensesSection: true,

  // Contractual Clauses Sections
  showGlobalSupplyChainClause: true,
  showTariffsAndDutiesClause: true,
  showContractualClausesSection: true,
  showStandardContractTerms: true,

  // Additional Customizations
  showItemBreakouts: true,
  showQuantityDetails: true,
  showUnitCosts: true,
  showLaborHours: true,
  showBurdenDetails: true,
  showAdjustmentDetails: true,
  compactView: false,
  showNotes: true,

  // Project totals display
  showProjectTotalsBreakdown: true,
  showSubtotalInCostSummary: true,

  // Cost Summary Category Controls
  showExtensionMaterialsInCostSummary: true,
  showQuotedMaterialsInCostSummary: true,
  showLaborInCostSummary: true,
  showEquipmentInCostSummary: true,
  showSubcontractorsInCostSummary: true,
  showGeneralExpensesInCostSummary: true,

  // Extension Materials Aggregation
  showExtensionBreakouts: true,

  // Notes Display
  showNotesInTables: true,

  // Expiration Date Settings
  showExpirationDate: true,
  expirationDays: 30,
  customExpirationDate: null,
};

const defaultPresets: ReportPreset[] = [
  {
    id: 'detailed',
    name: 'Detailed Report',
    description: 'Full detailed report with all sections and breakdowns',
    settings: { ...defaultSettings },
    isDefault: true,
    createdAt: new Date(),
  },
  {
    id: 'simple',
    name: 'Simple Report',
    description: 'Cost summary only with all totals, no detailed table sections',
    settings: {
      ...defaultSettings,
      // Hide all report sections above Cost Summary
      showMaterialsSection: false,
      showQuotesSection: false,
      showLaborSection: false,
      showEquipmentSection: false,
      showSubcontractorSection: false,
      showGeneralExpensesSection: false,
      // Only basic contractual terms for simple reports
      showGlobalSupplyChainClause: false,
      showTariffsAndDutiesClause: false,
      showContractualClausesSection: true,
      showStandardContractTerms: true,
      // Less detail for simple reports - aggregate extension materials
      showExtensionBreakouts: false,
      // Show subtotal for simple reports
      showSubtotalInCostSummary: true,
    },
    isDefault: true,
    createdAt: new Date(),
  },
  {
    id: 'minimal',
    name: 'Minimal Report',
    description: 'Project totals only, no breakdowns or sections',
    settings: {
      ...defaultSettings,
      // Hide all sections above Cost Summary
      showMaterialsSection: false,
      showQuotesSection: false,
      showLaborSection: false,
      showEquipmentSection: false,
      showSubcontractorSection: false,
      showGeneralExpensesSection: false,
      // No contractual clauses for minimal reports
      showGlobalSupplyChainClause: false,
      showTariffsAndDutiesClause: false,
      showContractualClausesSection: false,
      showStandardContractTerms: true,
      // Hide all details
      showDetailedBreakdown: false,
      showTaxDetails: false,
      showOverheadMarkup: false,
      showBaseCostColumn: false,
      showBurdenDetails: false,
      showAdjustmentDetails: false,
      showItemBreakouts: false,
      showQuantityDetails: false,
      showUnitCosts: false,
      showLaborHours: false,
      includeLaborRates: false,
      showNotes: false,
      compactView: true,
      // Only show final total, no breakdown
      showProjectTotalsBreakdown: false,
      showSubtotalInCostSummary: false,
      // Minimal detail for minimal reports - aggregate extension materials
      showExtensionBreakouts: false,
      // No notes for minimal reports
      showNotesInTables: false,
      // Set all cost summary categories to false in the minimal preset
      showExtensionMaterialsInCostSummary: false,
      showQuotedMaterialsInCostSummary: false,
      showLaborInCostSummary: false,
      showEquipmentInCostSummary: false,
      showSubcontractorsInCostSummary: false,
      showGeneralExpensesInCostSummary: false,
    },
    isDefault: true,
    createdAt: new Date(),
  },
];

export const useReportPresetsStore = create<ReportPresetsStore>()(
  persist(
    (set, get) => ({
      currentSettings: { ...defaultSettings },
      presets: [...defaultPresets],
      activePresetId: 'detailed',
      isModified: false,

      updateSetting: (key, value) => {
        set(state => ({
          currentSettings: {
            ...state.currentSettings,
            [key]: value,
          },
          isModified: true, // Mark as modified instead of clearing preset
        }));
      },

      updateSettings: settings => {
        set(state => ({
          currentSettings: {
            ...state.currentSettings,
            ...settings,
          },
          isModified: true,
        }));
      },

      savePreset: (name, description = '') => {
        const newPreset: ReportPreset = {
          id: `preset_${Date.now()}`,
          name,
          description,
          settings: { ...get().currentSettings },
          createdAt: new Date(),
        };

        set(state => ({
          presets: [...state.presets, newPreset],
          activePresetId: newPreset.id,
          isModified: false,
        }));
      },

      updatePreset: (presetId, name, description) => {
        const currentSettings = get().currentSettings;
        set(state => ({
          presets: state.presets.map(preset =>
            preset.id === presetId
              ? {
                  ...preset,
                  name: name || preset.name,
                  description: description !== undefined ? description : preset.description,
                  settings: { ...currentSettings },
                  lastUsed: new Date(),
                }
              : preset
          ),
          isModified: false,
        }));
      },

      loadPreset: presetId => {
        const preset = get().presets.find(p => p.id === presetId);
        if (preset) {
          // Ensure all default settings are included in case preset is missing new fields
          const mergedSettings = {
            ...defaultSettings,
            ...preset.settings,
          };

          set(state => ({
            currentSettings: mergedSettings,
            activePresetId: presetId,
            isModified: false,
            presets: state.presets.map(p =>
              p.id === presetId ? { ...p, lastUsed: new Date() } : p
            ),
          }));
        }
      },

      deletePreset: presetId => {
        set(state => ({
          presets: state.presets.filter(p => p.id !== presetId && !p.isDefault),
          activePresetId: state.activePresetId === presetId ? null : state.activePresetId,
        }));
      },

      duplicatePreset: (presetId, newName) => {
        const preset = get().presets.find(p => p.id === presetId);
        if (preset) {
          const newPreset: ReportPreset = {
            id: `preset_${Date.now()}`,
            name: newName,
            description: `Copy of ${preset.name}`,
            settings: { ...defaultSettings, ...preset.settings },
            createdAt: new Date(),
          };

          set(state => ({
            presets: [...state.presets, newPreset],
          }));
        }
      },

      applyDetailedPreset: () => {
        get().loadPreset('detailed');
      },

      applySimplePreset: () => {
        get().loadPreset('simple');
      },

      applyMinimalPreset: () => {
        get().loadPreset('minimal');
      },

      hasUnsavedChanges: () => {
        return get().isModified;
      },

      getActivePreset: () => {
        const { activePresetId, presets } = get();
        return activePresetId ? presets.find(p => p.id === activePresetId) || null : null;
      },

      resetToDefaults: () => {
        set({
          currentSettings: { ...defaultSettings },
          activePresetId: 'detailed',
          isModified: false,
        });
      },
    }),
    {
      name: 'report-presets-storage',
      version: 8, // Increment version to trigger migration for cost summary category controls
      migrate: (persistedState: any, version) => {
        // Migrate from version 3 to 4: add contractual clause fields
        if (version === 3) {
          const state = persistedState as any;

          // Update current settings with new contractual clause defaults
          if (state.currentSettings) {
            state.currentSettings = {
              ...defaultSettings,
              ...state.currentSettings,
            };
          }

          // Update all presets with new contractual clause defaults
          if (state.presets) {
            state.presets = state.presets.map((preset: any) => ({
              ...preset,
              settings: {
                ...defaultSettings,
                ...preset.settings,
              },
            }));
          }
        }

        // Migrate from version 4 to 5: add extension materials breakout display field
        if (version === 4) {
          const state = persistedState as any;

          // Update current settings with new breakout display defaults
          if (state.currentSettings) {
            state.currentSettings = {
              ...defaultSettings,
              ...state.currentSettings,
            };
          }

          // Update all presets with new breakout display defaults
          if (state.presets) {
            state.presets = state.presets.map((preset: any) => ({
              ...preset,
              settings: {
                ...defaultSettings,
                ...preset.settings,
              },
            }));
          }
        }

        // Migrate from version 5 to 6: add subtotal and notes display fields
        if (version === 5) {
          const state = persistedState as any;

          // Update current settings with new fields
          if (state.currentSettings) {
            state.currentSettings = {
              ...defaultSettings,
              ...state.currentSettings,
            };
          }

          // Update all presets with new fields
          if (state.presets) {
            state.presets = state.presets.map((preset: any) => ({
              ...preset,
              settings: {
                ...defaultSettings,
                ...preset.settings,
              },
            }));
          }
        }

        // Migrate from version 6 to 7: add expiration date fields
        if (version === 6) {
          const state = persistedState as any;

          // Update current settings with new expiration date fields
          if (state.currentSettings) {
            state.currentSettings = {
              ...defaultSettings,
              ...state.currentSettings,
            };
          }

          // Update all presets with new expiration date fields
          if (state.presets) {
            state.presets = state.presets.map((preset: any) => ({
              ...preset,
              settings: {
                ...defaultSettings,
                ...preset.settings,
              },
            }));
          }
        }

        // Migrate from version 7 to 8: add cost summary category controls
        if (version === 7) {
          const state = persistedState as any;

          // Update current settings with new cost summary category defaults
          if (state.currentSettings) {
            state.currentSettings = {
              ...defaultSettings,
              ...state.currentSettings,
            };
          }

          // Update all presets with new cost summary category defaults
          if (state.presets) {
            state.presets = state.presets.map((preset: any) => {
              const updatedSettings = {
                ...defaultSettings,
                ...preset.settings,
              };

              // Special handling for minimal preset - set all cost summary categories to false
              if (preset.id === 'minimal') {
                updatedSettings.showExtensionMaterialsInCostSummary = false;
                updatedSettings.showQuotedMaterialsInCostSummary = false;
                updatedSettings.showLaborInCostSummary = false;
                updatedSettings.showEquipmentInCostSummary = false;
                updatedSettings.showSubcontractorsInCostSummary = false;
                updatedSettings.showGeneralExpensesInCostSummary = false;
              }

              return {
                ...preset,
                settings: updatedSettings,
              };
            });
          }
        }

        return persistedState;
      },
    }
  )
);
